import booleanIntersects from '@turf/boolean-intersects';
import getBBox from '@turf/bbox';
import hexGrid from '@turf/hex-grid';
import union from '@turf/union';

// This really is a rectangle expansion of the bounding box.
const bufferBBox = (bb, d) => [bb[0] - d, bb[1] - d, bb[2] + d, bb[3] + d];

// using the bounding box given by bb,
//  pick a cell size that will divide the area into
//  at least minWidth cells.
const getCellSize = (bb, minWidth) => {
  const w = bb[2] - bb[0];
  const cellSizes = [0.2, 0.15, 0.1, 0.05, 0.01, 0.005, 0.001, 0.0005, 0.0001];

  for (let i = 0, ii = cellSizes.length; i < ii; i++) {
    if (w / cellSizes[i] > minWidth) {
      return cellSizes[i];
    }
  }
  // when in doubt, take the smallest cell size.
  return cellSizes.pop();
};

// Take in a collection and return a list of polygon features.
const decomposeCollection = collection => {
  if (collection.type === 'FeatureCollection') {
    return collection.features.flatMap(feature => decomposeCollection(feature));
  } else if (Array.isArray(collection)) {
    return collection.flatMap(feature => decomposeCollection(feature));
  }
  // assume this is a feature and not actually a collection.
  const geometry = collection.geometry;
  if (geometry.type.startsWith('Multi')) {
    // there is likely a more efficent string operation here,
    //  but this seemed to be the clearest way to write it
    const geomType = geometry.type.replace('Multi', '');
    // take the coordinates and decompose them
    return geometry.coordinates.map(coordinates => ({
      type: 'Feature',
      geometry: {
        type: geomType,
        coordinates,
      },
    }));
  }
  // non-multi-features are fine...
  return [
    {
      type: 'Feature',
      geometry,
    },
  ];
};

const makeHexGrid = collection => {
  const origBBox = getBBox(collection);
  const cellSize = getCellSize(origBBox, 60);

  // cellSize * 3 is used to account for the over run of the hexes
  //  from the bounding box.
  const bbox = bufferBBox(origBBox, cellSize * 3);
  const options = {units: 'degrees'};

  // create an x-ordered list of the features
  // this is used as a 1-dimensional spatial index of sorts.
  const orderedFeatures = decomposeCollection(collection.features)
    .map(f => ({
      ...f,
      _bbox: getBBox(f),
    }))
    .sort((a, b) => {
      return a._bbox[0] - b._bbox[0];
    });

  const len = orderedFeatures.length;

  // filter out hexGrid features which do not interect the original features.
  const outGrid = hexGrid(bbox, cellSize, options).features.filter(feature => {
    const fbbox = getBBox(feature);

    let i = 0;

    // this could be smarter and use some sort of bubble sort.
    while (i < len && orderedFeatures[i]._bbox[0] <= fbbox[2]) {
      // bbox intersection is ~10x faster, and this is a greedy algorithm.
      const polyBbox = orderedFeatures[i]._bbox;
      const bboxAsPolygon = {
        type: 'Polygon',
        coordinates: [
          [
            [polyBbox[0], polyBbox[1]],
            [polyBbox[2], polyBbox[1]],
            [polyBbox[2], polyBbox[3]],
            [polyBbox[0], polyBbox[3]],
            [polyBbox[0], polyBbox[1]],
          ],
        ],
      };

      if (booleanIntersects(feature, bboxAsPolygon)) {
        return true;
      }
      i++;
    }
    return false;
  });
  return outGrid;
};

export const simplifyByHex = collection => {
  const outGrid = makeHexGrid(collection);

  // dissolve the hex grid to a polygon
  let outFeature = {...outGrid[0]};
  for (let i = 1, ii = outGrid.length; i < ii; i++) {
    outFeature = union(outFeature, outGrid[i]);
  }

  return outFeature;
};
